import './Notifications.scss';

import cn from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import {
  NOTIFICATIONS_ENTITY_TYPE,
  NOTIFICATIONS_TYPES,
} from '@/common/constants';
import { useConfig } from '@/common/contexts/ConfigContext';
import { useNotificationsContext } from '@/common/contexts/NotificationsContext/NotificationsContext';
import useManageNotifications from '@/common/hooks/notifications/useManageNotifications';
import { ReactComponent as CloseIcon } from '@/ebat/assets/icons/close.svg';
import { ReactComponent as MoreIcon } from '@/ebat/assets/icons/more.svg';
import Loader from '@/ebat/components/blocks/Common/Loader/Loader';
import { useHeaderMenuContext } from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext';

import InfiniteScrollItemWrapper from '../../Products/InfiniteScrollItemWrapper/InfiniteScrollItemWrapper';
import Notification from './Notification/Notification';
import {
  NotificationsDefaultProps,
  NotificationsPropTypes,
} from './Notifications.proptypes';
import useNotifications from './useNotifications';

const Notifications = ({ className }) => {
  const { isEbat } = useConfig();
  const { t } = useTranslation();
  const {
    isFetching,
    onRead,
    allNotifications,
    orderNotifications,
    quoteNotifications,
    fetchNextPageAll,
    fetchNextPageOrder,
    fetchNextPageQuote,
    selectedType,
    hasNextPageAll,
    hasNextPageOrder,
    hasNextPageQuote,
  } = useNotificationsContext();
  const { handleClose } = useHeaderMenuContext();
  const {
    isDisabled: isDisabledUseManageNotifications,
    onUpdateNotifications,
  } = useManageNotifications({
    disabled: false,
    isLoading: false,
  });
  const observer = useRef(null);

  let currentNotifications;

  if (selectedType === 'all') {
    currentNotifications = allNotifications;
  } else if (selectedType === 'order') {
    currentNotifications = orderNotifications;
  } else {
    currentNotifications = quoteNotifications;
  }

  const {
    getMoreActionsClassNames,
    isDisabled,
    isMoreActionsOpen,
    notificationsNotReaded,
    notificationsNotArchived,
    onGetButtonClassNames,
    onSelectedType,
    onToggleMoreActions,
  } = useNotifications({
    disabled: isDisabledUseManageNotifications.disabled,
    isLoading: isDisabledUseManageNotifications.loading,
    notifications: currentNotifications,
    onUpdateNotifications,
  });

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          ((hasNextPageAll && selectedType === NOTIFICATIONS_ENTITY_TYPE.ALL) ||
            (hasNextPageOrder &&
              selectedType === NOTIFICATIONS_ENTITY_TYPE.ORDER) ||
            (hasNextPageQuote &&
              selectedType === NOTIFICATIONS_ENTITY_TYPE.QUOTE))
        ) {
          if (selectedType === NOTIFICATIONS_ENTITY_TYPE.ORDER) {
            fetchNextPageOrder();
          } else if (selectedType === NOTIFICATIONS_ENTITY_TYPE.QUOTE) {
            fetchNextPageQuote();
          } else {
            fetchNextPageAll();
          }
        }
      });
    });

    const lastNotificationElement = document.querySelector(
      '.list--notifications li:last-child',
    );
    if (lastNotificationElement) {
      observer.current.observe(lastNotificationElement);
    }

    return () => observer.current?.disconnect();
  }, [
    currentNotifications,
    fetchNextPageAll,
    fetchNextPageOrder,
    fetchNextPageQuote,
    hasNextPageAll,
    hasNextPageOrder,
    hasNextPageQuote,
    selectedType,
  ]);

  const onGetCount = (entityType = null) => {
    if (entityType) {
      return (
        currentNotifications?.data.filter(
          (notification) =>
            notification.entity_type === entityType && !notification.is_read,
        ).length || 0
      );
    }

    return (
      currentNotifications?.data.filter((notification) => !notification.is_read)
        .length || 0
    );
  };

  /**
   * Generate more actions component
   * @type {component}
   */
  const MoreActions = (
    <div className="more-actions">
      <Button
        className={getMoreActionsClassNames}
        disabled={isDisabled.disabled}
        onClick={onToggleMoreActions}
        variant="secondary-light"
      >
        <MoreIcon className="button__icon" />
      </Button>
      {isMoreActionsOpen && (
        <div className="more-actions__content">
          {onGetCount() > 0 && (
            <Button
              className="button--with-icon"
              disabled={isDisabled.disabled}
              onClick={() =>
                onRead(NOTIFICATIONS_TYPES.READ, notificationsNotReaded)
              }
            >
              <span className="circle">
                <span className="point" />
              </span>
              <span className="label">
                {t('ebat:entities:layout.notification.put_all_read')}
              </span>
            </Button>
          )}
          <Button
            className="button--with-icon"
            disabled={isDisabled.disabled}
            onClick={() =>
              onRead(NOTIFICATIONS_TYPES.ARCHIVE, notificationsNotArchived)
            }
          >
            <span className="circle">
              <CloseIcon className="icon" />
            </span>
            <span className="label">
              {t('ebat:entities:layout.notification.put_all_archived')}
            </span>
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={cn(className, 'notifications')}>
      <div className="notifications__top">
        <Button className="button--actions" onClick={handleClose} withIcon>
          <CloseIcon className="button__icon" />
          <span className="button__label">
            {t('ebat:entities:layout.notification.notifications')}
          </span>
          {allNotifications?.meta?.total_items_unread > 0 && (
            <span className="notifications-number">
              {allNotifications?.meta?.total_items_unread}
            </span>
          )}
        </Button>
      </div>

      <div className="notifications__header">
        <Button
          className={onGetButtonClassNames(NOTIFICATIONS_ENTITY_TYPE.ALL)}
          disabled={isDisabled.disabled}
          onClick={onSelectedType}
          value={NOTIFICATIONS_ENTITY_TYPE.ALL}
        >
          {t('ebat:entities:layout.notification.all', {
            count: allNotifications?.meta?.total_items_unread,
          })}
        </Button>
        <Button
          className={onGetButtonClassNames(NOTIFICATIONS_ENTITY_TYPE.ORDER)}
          disabled={isDisabled.disabled}
          onClick={onSelectedType}
          value={NOTIFICATIONS_ENTITY_TYPE.ORDER}
        >
          {t('ebat:entities:layout.notification.orders', {
            count: allNotifications?.meta?.total_order_items_unread,
          })}
        </Button>
        {isEbat && (
          <Button
            className={onGetButtonClassNames(NOTIFICATIONS_ENTITY_TYPE.QUOTE)}
            onClick={onSelectedType}
            value={NOTIFICATIONS_ENTITY_TYPE.QUOTE}
            isLoading={isDisabled.loading}
          >
            {t('ebat:entities:layout.notification.quotes', {
              count: allNotifications?.meta?.total_quote_items_unread,
            })}
          </Button>
        )}
        {currentNotifications?.data.length > 0 && MoreActions}
      </div>
      {currentNotifications ? (
        <div className="notifications__content">
          <ul className="list list--notifications">
            {currentNotifications.data.map((notification, i) => (
              <InfiniteScrollItemWrapper
                key={`notification-${notification.id}-${i}`}
                page={currentNotifications.meta?.current_page}
                wrapperObserver={observer.current}
                className="notification-wrapper"
                context="notification"
              >
                <Notification
                  datetime={notification.datetime}
                  disabled={isDisabled.disabled}
                  entityExists={notification.entity_exists}
                  entityId={notification.entity_id}
                  id={notification.id}
                  isLoading={isDisabled.loading}
                  isRead={notification.is_read}
                  message={notification.message}
                  object={notification.object}
                  onClose={handleClose}
                  onRead={onRead}
                  type={notification.entity_type}
                  typeOrigin={notification.entity_type_origin}
                />
              </InfiniteScrollItemWrapper>
            ))}
          </ul>
          {isFetching && <Loader type="full" />}
        </div>
      ) : (
        <Loader className="notifications__loader" />
      )}
      <div className="notifications__actions">
        {MoreActions}
        <Button
          className="button--actions"
          isLoading={isDisabled.loading}
          onClick={handleClose}
          variant="primary"
        >
          <CloseIcon className="button__icon" />
        </Button>
      </div>
    </div>
  );
};

Notifications.defaultProps = {
  ...NotificationsDefaultProps,
};

Notifications.propTypes = {
  ...NotificationsPropTypes,
};

export default Notifications;
